import React, { useContext, useEffect, useState, useMemo } from "react";
import { useRevalidator, useNavigate } from "react-router-dom";
import {
  Box,
  Icon,
  Avatar,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Checkbox,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  Badge,
  HStack,
  VStack,
  Stack,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Skeleton,
  SkeletonText,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Divider,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  useBreakpointValue,
  ButtonGroup,
  Switch,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Spacer,
} from "@chakra-ui/react";
import {
  FiUsers,
  FiSearch,
  FiEdit3,
  FiTrash2,
  FiMoreVertical,
  FiUser,
  FiSettings,
  FiChevronUp,
  FiChevronDown,
  FiUserPlus,
  FiRefreshCw,
  FiSend,
} from "react-icons/fi";
import { MdCancel, MdCheckCircle } from "react-icons/md";
import { AddIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import getFullName from "../../../../helpers/FullName";
import AuthContext from "../../../../contexts/Auth";
import client from "../../../../helpers/Client";
import urlToId from "../../../../helpers/UrlToId";
import { useTranslation } from "react-i18next";
import Pagination from "../../../../components/pagination/Pagination";

function ImprovedEmployeeNameWithAvatar({ employee }) {
  const textColor = "gray.700";
  const subTextColor = "gray.500";

  return (
    <HStack spacing={3}>
      <Avatar
        size="md"
        name={getFullName(employee.user)}
        src={employee.user?.profile_picture}
        bg="blue.500"
        color="white"
        fontWeight="bold"
      />
      <VStack align="start" spacing={0}>
        <Text fontWeight="semibold" color={textColor} fontSize="sm">
          {getFullName(employee.user)}
        </Text>
        <Text fontSize="xs" color={subTextColor}>
          {employee.user?.email}
        </Text>
        {employee.user?.phone && (
          <Text fontSize="xs" color={subTextColor}>
            {employee.user.phone}
          </Text>
        )}
      </VStack>
    </HStack>
  );
}

function EnhancedTrueFalseIcon({
  value,
  trueLabel = "Evet",
  falseLabel = "Hayır",
}) {
  return (
    <Tooltip label={value ? trueLabel : falseLabel}>
      <Badge
        colorScheme={value ? "green" : "gray"}
        variant={value ? "solid" : "outline"}
        px={3}
        py={1}
        borderRadius="full"
        fontSize="xs"
        fontWeight="semibold"
      >
        <Icon as={value ? MdCheckCircle : MdCancel} mr={1} boxSize={3} />
        {value ? trueLabel : falseLabel}
      </Badge>
    </Tooltip>
  );
}

function EmployeeStats({ employees }) {
  const cardBg = "white";
  const borderColor = "gray.200";

  const stats = useMemo(() => {
    const totalEmployees = employees.length;
    const managers = employees.filter((emp) => emp.is_manager).length;
    const teamMembers = employees.filter(
      (emp) => emp.team_memberships.length > 0
    ).length;
    const teamLeaders = employees.filter((emp) =>
      emp.team_memberships.some((membership) => membership.is_lead)
    ).length;

    return {
      total: totalEmployees,
      managers,
      teamMembers,
      teamLeaders,
      withoutTeam: totalEmployees - teamMembers,
    };
  }, [employees]);

  return (
    <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={6}>
      <Stat
        bg={cardBg}
        p={4}
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        shadow="sm"
      >
        <StatLabel fontSize="xs" color="gray.500">
          Toplam Personel
        </StatLabel>
        <StatNumber fontSize="2xl" color="blue.500">
          {stats.total}
        </StatNumber>
      </Stat>

      <Stat
        bg={cardBg}
        p={4}
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        shadow="sm"
      >
        <StatLabel fontSize="xs" color="gray.500">
          Yöneticiler
        </StatLabel>
        <StatNumber fontSize="2xl" color="purple.500">
          {stats.managers}
        </StatNumber>
      </Stat>

      <Stat
        bg={cardBg}
        p={4}
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        shadow="sm"
      >
        <StatLabel fontSize="xs" color="gray.500">
          Takım Üyeleri
        </StatLabel>
        <StatNumber fontSize="2xl" color="green.500">
          {stats.teamMembers}
        </StatNumber>
      </Stat>

      <Stat
        bg={cardBg}
        p={4}
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        shadow="sm"
      >
        <StatLabel fontSize="xs" color="gray.500">
          Takım Liderleri
        </StatLabel>
        <StatNumber fontSize="2xl" color="orange.500">
          {stats.teamLeaders}
        </StatNumber>
      </Stat>

      <Stat
        bg={cardBg}
        p={4}
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        shadow="sm"
      >
        <StatLabel fontSize="xs" color="gray.500">
          Takımsız
        </StatLabel>
        <StatNumber fontSize="2xl" color="red.500">
          {stats.withoutTeam}
        </StatNumber>
      </Stat>
    </SimpleGrid>
  );
}

// Advanced Filters Component
function AdvancedFilters({ filters, onFiltersChange, employees }) {
  const cardBg = "white";
  const borderColor = "gray.200";
  const isMobile = useBreakpointValue({ base: true, md: false });

  const resellers = useMemo(() => {
    const uniqueResellers = [
      ...new Set(employees.map((emp) => emp.reseller_name)),
    ];
    return uniqueResellers.filter(Boolean);
  }, [employees]);

  const teams = useMemo(() => {
    const allTeams = employees.flatMap((emp) =>
      emp.team_memberships.map((m) => m.team.name)
    );
    return [...new Set(allTeams)];
  }, [employees]);

  return (
    <Box
      bg={cardBg}
      p={4}
      borderRadius="xl"
      border="1px"
      borderColor={borderColor}
      mb={4}
    >
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={4}
        wrap={isMobile ? "nowrap" : "wrap"}
      >
        <InputGroup maxW={isMobile ? "100%" : "300px"}>
          <InputLeftElement>
            <Icon as={FiSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Personel ara..."
            value={filters.search}
            onChange={(e) =>
              onFiltersChange({ ...filters, search: e.target.value })
            }
          />
        </InputGroup>

        <Select
          placeholder="Bayi Seç"
          maxW={isMobile ? "100%" : "200px"}
          value={filters.reseller}
          onChange={(e) =>
            onFiltersChange({ ...filters, reseller: e.target.value })
          }
        >
          {resellers.map((reseller) => (
            <option key={reseller} value={reseller}>
              {reseller}
            </option>
          ))}
        </Select>

        <Select
          placeholder="Takım Seç"
          maxW={isMobile ? "100%" : "200px"}
          value={filters.team}
          onChange={(e) =>
            onFiltersChange({ ...filters, team: e.target.value })
          }
        >
          {teams.map((team) => (
            <option key={team} value={team}>
              {team}
            </option>
          ))}
        </Select>

        <Select
          placeholder="Rol Filtresi"
          maxW={isMobile ? "100%" : "150px"}
          value={filters.role}
          onChange={(e) =>
            onFiltersChange({ ...filters, role: e.target.value })
          }
        >
          <option value="manager">Yöneticiler</option>
          <option value="team_lead">Takım Liderleri</option>
          <option value="team_member">Takım Üyeleri</option>
          <option value="no_team">Takımsız</option>
        </Select>

        <Select
          placeholder="Telefon Filtresi"
          maxW={isMobile ? "100%" : "180px"}
          value={filters.phone}
          onChange={(e) =>
            onFiltersChange({ ...filters, phone: e.target.value })
          }
        >
          <option value="with_phone">Telefonu olanlar</option>
          <option value="without_phone">Telefonu olmayanlar</option>
        </Select>

        <Select
          placeholder="Giriş İzni"
          maxW={isMobile ? "100%" : "160px"}
          value={filters.login}
          onChange={(e) =>
            onFiltersChange({ ...filters, login: e.target.value })
          }
        >
          <option value="login_enabled">Giriş izni açık</option>
          <option value="login_disabled">Giriş izni kapalı</option>
        </Select>

        <Select
          placeholder="Giriş Durumu"
          maxW={isMobile ? "100%" : "180px"}
          value={filters.loginActivity}
          onChange={(e) =>
            onFiltersChange({ ...filters, loginActivity: e.target.value })
          }
        >
          <option value="never_logged_in">Hiç giriş yapmamış olanlar</option>
          <option value="low_activity">
            Son dönemde çok az giriş yapanlar
          </option>
        </Select>

        <Button
          size={isMobile ? "md" : "sm"}
          width={isMobile ? "100%" : "auto"}
          variant="ghost"
          onClick={() =>
            onFiltersChange({
              search: "",
              reseller: "",
              team: "",
              role: "",
              phone: "",
              login: "",
              loginActivity: "",
            })
          }
        >
          Temizle
        </Button>
      </Stack>

      {/* Active Filters */}
      {(filters.search ||
        filters.reseller ||
        filters.team ||
        filters.role ||
        filters.phone ||
        filters.login ||
        filters.loginActivity) && (
        <Box mt={3}>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Aktif Filtreler:
          </Text>
          <Wrap>
            {filters.search && (
              <WrapItem>
                <Tag size="sm" colorScheme="blue">
                  <TagLabel>Arama: {filters.search}</TagLabel>
                  <TagCloseButton
                    onClick={() => onFiltersChange({ ...filters, search: "" })}
                  />
                </Tag>
              </WrapItem>
            )}
            {filters.reseller && (
              <WrapItem>
                <Tag size="sm" colorScheme="purple">
                  <TagLabel>Bayi: {filters.reseller}</TagLabel>
                  <TagCloseButton
                    onClick={() =>
                      onFiltersChange({ ...filters, reseller: "" })
                    }
                  />
                </Tag>
              </WrapItem>
            )}
            {filters.team && (
              <WrapItem>
                <Tag size="sm" colorScheme="green">
                  <TagLabel>Takım: {filters.team}</TagLabel>
                  <TagCloseButton
                    onClick={() => onFiltersChange({ ...filters, team: "" })}
                  />
                </Tag>
              </WrapItem>
            )}
            {filters.role && (
              <WrapItem>
                <Tag size="sm" colorScheme="orange">
                  <TagLabel>Rol: {filters.role}</TagLabel>
                  <TagCloseButton
                    onClick={() => onFiltersChange({ ...filters, role: "" })}
                  />
                </Tag>
              </WrapItem>
            )}
            {filters.phone && (
              <WrapItem>
                <Tag size="sm" colorScheme="teal">
                  <TagLabel>
                    {filters.phone === "with_phone"
                      ? "Telefonu olanlar"
                      : "Telefonu olmayanlar"}
                  </TagLabel>
                  <TagCloseButton
                    onClick={() => onFiltersChange({ ...filters, phone: "" })}
                  />
                </Tag>
              </WrapItem>
            )}
            {filters.login && (
              <WrapItem>
                <Tag size="sm" colorScheme="pink">
                  <TagLabel>
                    {filters.login === "login_enabled"
                      ? "Giriş izni açık"
                      : "Giriş izni kapalı"}
                  </TagLabel>
                  <TagCloseButton
                    onClick={() => onFiltersChange({ ...filters, login: "" })}
                  />
                </Tag>
              </WrapItem>
            )}
            {filters.loginActivity && (
              <WrapItem>
                <Tag size="sm" colorScheme="cyan">
                  <TagLabel>
                    {filters.loginActivity === "never_logged_in"
                      ? "Hiç giriş yapmamış olanlar"
                      : "Son dönemde çok az giriş yapanlar"}
                  </TagLabel>
                  <TagCloseButton
                    onClick={() =>
                      onFiltersChange({ ...filters, loginActivity: "" })
                    }
                  />
                </Tag>
              </WrapItem>
            )}
          </Wrap>
        </Box>
      )}
    </Box>
  );
}

// Main Improved Employee Table Component
export default function ImprovedEmployeeTableView({
  employees,
  onAddToTeam,
  onEditEmployee,
  onDeleteEmployee,
  onCreateTeam,
  onRemoveFromTeam,
  isLoading = false,
}) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const revalidator = useRevalidator();

  // All color mode values at the top level to avoid hook order issues
  const bgGradient = "linear(135deg, white 0%, gray.50 100%)";
  const headerBg = "linear(135deg, red.600 0%, red.900 100%)";
  const cardBg = "white";
  const tableBg = "gray.50";
  const rowHoverBg = "blue.50";
  const textColor = "gray.700";
  const borderColor = "gray.200";
  const hoverBgTable = "gray.100";
  const shadowColor = "0 10px 25px rgba(0,0,0,0.1)";

  // State management
  const [filters, setFilters] = useState({
    search: "",
    reseller: "",
    team: "",
    role: "",
    phone: "",
    login: "",
    loginActivity: "",
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedUrls, setSelectedUrls] = useState(new Set());
  const [visibleCols, setVisibleCols] = useState({
    dealer: true,
    roles: true,
    login: true,
    teams: true,
  });
  const [detailEmployee, setDetailEmployee] = useState(null);
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsLogsLoading, setSmsLogsLoading] = useState(false);
  const [activitySummaryMap, setActivitySummaryMap] = useState({});
  const [activitySummaryLoading, setActivitySummaryLoading] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);

  const detailSummaryId = detailEmployee ? urlToId(detailEmployee.url) : null;
  const detailSummary =
    detailSummaryId && activitySummaryMap[detailSummaryId]
      ? activitySummaryMap[detailSummaryId]
      : null;

  // Permission checks
  const isSuperUser = auth.data?.user?.is_superuser;
  const isManager = auth.data?.user?.employee?.is_manager;

  const isMobile = useBreakpointValue({ base: true, md: false });

  const toggleManager = async (employee) => {
    try {
      const userId = urlToId(employee.user.url);
      const nextVal = !employee.is_manager;
      await client.put(`/users/${userId}/set_roles/`, { is_manager: nextVal });
      toast({
        title: nextVal
          ? t("employee.make_manager")
          : t("employee.remove_manager_privilege"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      revalidator.revalidate();
    } catch (e) {
      toast({
        title: t("messages.operation_failed"),
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const toggleServicer = async (employee) => {
    try {
      const userId = urlToId(employee.user.url);
      const { data } = await client.get(`/users/${userId}/`);
      const nextVal = !data.is_servicer;
      await client.put(`/users/${userId}/set_roles/`, { is_servicer: nextVal });
      toast({
        title: t("employee.toggle_servicer_status"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      revalidator.revalidate();
    } catch (e) {
      toast({
        title: t("messages.operation_failed"),
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const toggleLoginEnabled = async (employee) => {
    try {
      const employeeId = urlToId(employee.url);
      const nextVal = !employee.login_enabled;
      await client.post(`/employees/${employeeId}/set_login_enabled/`, {
        login_enabled: nextVal,
      });
      toast({
        title: nextVal
          ? t("employee.login_permission_enabled")
          : t("employee.login_permission_disabled"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      revalidator.revalidate();
    } catch (e) {
      toast({
        title: t("messages.operation_failed"),
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const sendEmployeeCredentials = async (employee) => {
    try {
      const employeeId = urlToId(employee.url);
      await client.post(`/employees/${employeeId}/send_credentials/`);
      toast({
        title: t("employee.send_credentials_success"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: t("employee.send_credentials_failed"),
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  // Filter and sort employees
  const filteredAndSortedEmployees = useMemo(() => {
    let filtered = employees.filter((employee) => {
      const summary = activitySummaryMap[employee.id];
      const totalLogins = summary?.total_logins || 0;
      const periodLogins = summary?.period_logins || 0;

      const matchesSearch =
        !filters.search ||
        getFullName(employee.user)
          .toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        employee.user?.email
          ?.toLowerCase()
          .includes(filters.search.toLowerCase());

      const matchesReseller =
        !filters.reseller || employee.reseller_name === filters.reseller;

      const matchesTeam =
        !filters.team ||
        employee.team_memberships.some((m) => m.team.name === filters.team);

      const matchesRole =
        !filters.role ||
        (filters.role === "manager" && employee.is_manager) ||
        (filters.role === "team_lead" &&
          employee.team_memberships.some((m) => m.is_lead)) ||
        (filters.role === "team_member" &&
          employee.team_memberships.length > 0) ||
        (filters.role === "no_team" && employee.team_memberships.length === 0);

      const hasPhone = !!employee.phone_number;
      const matchesPhone =
        !filters.phone ||
        (filters.phone === "with_phone" && hasPhone) ||
        (filters.phone === "without_phone" && !hasPhone);

      const matchesLogin =
        !filters.login ||
        (filters.login === "login_enabled" && employee.login_enabled) ||
        (filters.login === "login_disabled" && !employee.login_enabled);

      const matchesLoginActivity =
        !filters.loginActivity ||
        (filters.loginActivity === "never_logged_in" && totalLogins === 0) ||
        (filters.loginActivity === "low_activity" &&
          totalLogins > 0 &&
          periodLogins <= 1);

      return (
        matchesSearch &&
        matchesReseller &&
        matchesTeam &&
        matchesRole &&
        matchesPhone &&
        matchesLogin &&
        matchesLoginActivity
      );
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case "name":
            aValue = getFullName(a.user).toLowerCase();
            bValue = getFullName(b.user).toLowerCase();
            break;
          case "reseller":
            aValue = a.reseller_name || "";
            bValue = b.reseller_name || "";
            break;
          case "teams":
            aValue = a.team_memberships.length;
            bValue = b.team_memberships.length;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [employees, filters, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(
    filteredAndSortedEmployees.length / itemsPerPage
  );
  const paginatedEmployees = filteredAndSortedEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Sorting handler
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    const fetchSummary = async () => {
      setActivitySummaryLoading(true);
      try {
        const { data } = await client.get(
          "/employees/activity_summary/?days=30"
        );
        const map = {};
        if (data && Array.isArray(data.employees)) {
          data.employees.forEach((item) => {
            if (item && typeof item.employee_id !== "undefined") {
              map[item.employee_id] = item;
            }
          });
        }
        setActivitySummaryMap(map);
      } catch (e) {
        toast({
          title: t("messages.operation_failed"),
          status: "error",
          duration: 2500,
          isClosable: true,
        });
      } finally {
        setActivitySummaryLoading(false);
      }
    };
    fetchSummary();
  }, [toast, t]);

  const toggleSelectAllCurrentPage = () => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      const pageUrls = paginatedEmployees.map((e) => e.url);
      const allSelected = pageUrls.every((u) => next.has(u));
      if (allSelected) {
        pageUrls.forEach((u) => next.delete(u));
      } else {
        pageUrls.forEach((u) => next.add(u));
      }
      return next;
    });
  };

  const toggleSelectOne = (employee) => {
    const url = employee.url;
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const anySelected = selectedUrls.size > 0;

  const bulkSetLoginEnabled = async (enabled) => {
    if (!anySelected) return;
    try {
      const targets = employees.filter((e) => selectedUrls.has(e.url));
      await Promise.all(
        targets.map((employee) => {
          const employeeId = urlToId(employee.url);
          return client.post(`/employees/${employeeId}/set_login_enabled/`, {
            login_enabled: enabled,
          });
        })
      );
      toast({
        title: enabled
          ? t("employee.login_permission_enabled")
          : t("employee.login_permission_disabled"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setSelectedUrls(new Set());
      revalidator.revalidate();
    } catch (e) {
      toast({
        title: t("messages.operation_failed"),
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const bulkSendReminder = async () => {
    if (!anySelected) return;
    try {
      const targets = employees.filter((e) => selectedUrls.has(e.url));
      const ids = targets.map((employee) => urlToId(employee.url));
      await client.post(`/employees/send_bulk_reminder/`, {
        employee_ids: ids,
      });
      toast({
        title: "Hatırlatma SMS'leri gönderildi",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setSelectedUrls(new Set());
      revalidator.revalidate();
    } catch (e) {
      toast({
        title: t("messages.operation_failed"),
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const toggleVisibleCol = (key) => {
    setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const formatActivityAction = (action) => {
    if (action === "login_enabled_changed") return "Giriş izni değiştirildi";
    if (action === "team_member_added") return "Takıma eklendi";
    if (action === "team_member_removed") return "Takımdan çıkarıldı";
    if (action === "credentials_sent") return "Giriş bilgileri gönderildi";
    return action;
  };

  const [teamNameById, setTeamNameById] = useState({});
  const openDetail = async (employee) => {
    if (!isSuperUser) return;
    setDetailEmployee(employee);
    setSmsLogs([]);
    setSmsLogsLoading(true);
    setActivityLogs([]);
    setActivityLogsLoading(true);
    try {
      // Always refresh the selected employee to reflect latest team memberships
      const fresh = await client.get(employee.url, { disableCache: true }).then(r => Array.isArray(r?.data) ? r.data[0] : r.data).catch(() => null);
      if (fresh) setDetailEmployee(fresh);
      const employeeId = urlToId((fresh && fresh.url) || employee.url);
      // Fallback: if memberships not present, fetch via members endpoint and resolve team names
      try {
        const currentMems = (fresh && (fresh.team_memberships || fresh.memberships)) || [];
        if (!currentMems || currentMems.length === 0) {
          const memRes = await client.get('/members/', { params: { employee: employeeId }, disableCache: true });
          const data = memRes?.data;
          const items = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
          if (items && items.length) {
            const mems = [];
            for (const m of items) {
              const teamUrl = m?.team;
              let teamName = null;
              if (teamUrl) {
                const tid = urlToId(teamUrl);
                if (teamNameById[tid]) {
                  teamName = teamNameById[tid];
                } else {
                  try {
                    const tr = await client.get(`/teams/${tid}/`, { disableCache: true });
                    teamName = tr?.data?.name || `Takım #${tid}`;
                  } catch {
                    teamName = `Takım #${tid}`;
                  }
                }
              }
              mems.push({ is_lead: !!m?.is_lead, team: { name: teamName } });
            }
            if (mems.length) {
              setDetailEmployee(prev => prev ? ({ ...prev, team_memberships: mems }) : prev);
            }
          }
        }
      } catch (_) {}

      // Fetch logs (handle permissions gracefully)
      let act = [];
      try {
        const activityRes = await client.get(`/employees/activity_logs/?employee=${employeeId}&limit=100`);
        act = activityRes.data || [];
      } catch (e) {
        // ignore, toast below
      }
      let sms = [];
      try {
        const smsRes = await client.get(`/employees/${employeeId}/sms_logs/`);
        sms = smsRes.data || [];
      } catch (e) {
        // ignore, toast below
      }
      // Resolve team names referenced in logs (team_id)
      const neededIds = new Set();
      (act || []).forEach(log => {
        const ov = log?.old_value || {};
        const nv = log?.new_value || {};
        if (ov.team_id && !teamNameById[ov.team_id]) neededIds.add(ov.team_id);
        if (nv.team_id && !teamNameById[nv.team_id]) neededIds.add(nv.team_id);
      });
      if (neededIds.size) {
        const reqs = Array.from(neededIds).map(id =>
          client.get(`/teams/${id}/`).then(r => [id, r?.data?.name || `Takım #${id}`]).catch(() => [id, `Takım #${id}`])
        );
        const pairs = await Promise.allSettled(reqs);
        const next = { ...teamNameById };
        pairs.forEach(p => {
          if (p.status === 'fulfilled') {
            const [id, name] = p.value;
            next[id] = name;
          }
        });
        setTeamNameById(next);
      }
      setSmsLogs(sms);
      setActivityLogs(act);
    } catch (e) {
      const status = e?.response?.status;
      toast({
        title: status === 403 ? "Erişim izniniz yok" : t("messages.operation_failed"),
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setSmsLogsLoading(false);
      setActivityLogsLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailEmployee(null);
    setSmsLogs([]);
    setActivityLogs([]);
  };

  if (isLoading) {
    return (
      <Box p={6}>
        <Skeleton height="200px" mb={4} />
        <SkeletonText mt={4} noOfLines={8} spacing={4} />
      </Box>
    );
  }

  return (
    <Box
      bgGradient={bgGradient}
      borderRadius="2xl"
      p={isMobile ? 2 : 6}
      shadow="2xl"
      border="1px"
      borderColor={borderColor}
    >
      {/* Enhanced Header */}
      <Box
        bgGradient={headerBg}
        borderRadius="xl"
        p={isMobile ? 4 : 6}
        mb={6}
        shadow="lg"
      >
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <HStack spacing={4}>
            <Icon as={FiUsers} color="white" boxSize={isMobile ? 8 : 10} />
            <VStack align="start" spacing={1}>
              <Text
                fontSize={isMobile ? "2xl" : "3xl"}
                fontWeight="bold"
                color="white"
                letterSpacing="tight"
              >
                {t("employee.employee_management")}
              </Text>
              <Text fontSize="md" color="whiteAlpha.800">
                {filteredAndSortedEmployees.length} personel •
              </Text>
            </VStack>
          </HStack>

          {(isSuperUser || isManager) && (
            <ButtonGroup
              spacing={2}
              size={isMobile ? "sm" : "md"}
              width={isMobile ? "100%" : "auto"}
            >
              <SimpleGrid
                columns={{ base: 1, sm: 2 }}
                spacing={isMobile ? 3 : 6}
                mb={isMobile ? 0 : 5}
                width="100%"
              >
                <Button
                  onClick={() => onCreateTeam && onCreateTeam()}
                  leftIcon={<FiUsers />}
                  bg="white"
                  color="gray.700"
                  _hover={{
                    bg: "gray.100",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                  }}
                  transition="all 0.3s ease"
                  borderRadius="xl"
                  px={isMobile ? 3 : 6}
                  py={3}
                  fontWeight="semibold"
                  width="100%"
                >
                  {t("employee.create_team")}
                </Button>
                <Button
                  onClick={() => navigate("/calisan-ekle/")}
                  leftIcon={<FiUserPlus />}
                  bg="white"
                  color="gray.700"
                  _hover={{
                    bg: "gray.100",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                  }}
                  transition="all 0.3s ease"
                  borderRadius="xl"
                  px={isMobile ? 3 : 6}
                  py={3}
                  fontWeight="semibold"
                  width="100%"
                >
                  {t("employee.add_employee")}
                </Button>
              </SimpleGrid>
            </ButtonGroup>
          )}
        </Flex>
      </Box>

      {/* Statistics */}
      <EmployeeStats employees={employees} />

      {/* Advanced Filters */}
      <AdvancedFilters
        filters={filters}
        onFiltersChange={setFilters}
        employees={employees}
      />

      {/* Table Actions */}
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="sm" color="gray.500">
          {filteredAndSortedEmployees.length} sonuçtan{" "}
          {paginatedEmployees.length} tanesi gösteriliyor
        </Text>
        <HStack spacing={2}>
          {isSuperUser && anySelected && (
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.600">
                {selectedUrls.size} personel seçili
              </Text>
              {isSuperUser && (
                <>
                  <Button
                    size="xs"
                    colorScheme="green"
                    variant="solid"
                    onClick={() => bulkSetLoginEnabled(true)}
                  >
                    Giriş izni aç
                  </Button>
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="outline"
                    onClick={() => bulkSetLoginEnabled(false)}
                  >
                    Giriş izni kapat
                  </Button>
                </>
              )}
              <Button
                size="xs"
                leftIcon={<FiSend />}
                colorScheme="blue"
                variant="outline"
                onClick={bulkSendReminder}
              >
                Hatırlatma SMS
              </Button>
            </HStack>
          )}
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="outline"
            >
              Görünüm
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => toggleVisibleCol("dealer")}>
                <Checkbox isChecked={visibleCols.dealer} mr={2} pointerEvents="none" />
                Bayi
              </MenuItem>
              <MenuItem onClick={() => toggleVisibleCol("roles")}>
                <Checkbox isChecked={visibleCols.roles} mr={2} pointerEvents="none" />
                Roller
              </MenuItem>
              <MenuItem onClick={() => toggleVisibleCol("login")}>
                <Checkbox isChecked={visibleCols.login} mr={2} pointerEvents="none" />
                Giriş izni
              </MenuItem>
              <MenuItem onClick={() => toggleVisibleCol("teams")}>
                <Checkbox isChecked={visibleCols.teams} mr={2} pointerEvents="none" />
                Takımlar
              </MenuItem>
            </MenuList>
          </Menu>
          <Button size="sm" variant="outline" leftIcon={<FiRefreshCw />} onClick={() => revalidator.revalidate()}>
            {t("common.refresh")}
          </Button>
        </HStack>
      </Flex>

      {/* Enhanced Table */}
      {isMobile ? (
        paginatedEmployees.length > 0 ? (
          <SimpleGrid columns={1} spacing={4}>
            {paginatedEmployees.map((employee) => (
              <Box
                key={urlToId(employee.url)}
                bg={cardBg}
                borderRadius="xl"
                shadow="lg"
                p={4}
                border="1px"
                borderColor={borderColor}
              >
                <VStack align="stretch" spacing={4}>
                  <Flex justify="space-between" align="start">
                    <ImprovedEmployeeNameWithAvatar employee={employee} />

                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="sm"
                        aria-label="Options"
                      />
                      <MenuList>
                        <MenuItem
                          icon={<FiEdit3 />}
                          onClick={() => onEditEmployee(employee)}
                        >
                          {t("common.edit")}
                        </MenuItem>
                        <MenuItem
                          icon={<FiTrash2 />}
                          color="red.500"
                          onClick={() => onDeleteEmployee(employee)}
                        >
                          {t("common.delete")}
                        </MenuItem>
                        {onAddToTeam && (
                          <MenuItem
                            icon={<FiUserPlus />}
                            onClick={() => onAddToTeam(employee)}
                          >
                            {t("employee.add_to_team")}
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Flex>
                  {isSuperUser && (
                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" color="gray.500">
                        {t("employee.dealer")}
                      </Text>
                      <Badge
                        colorScheme="blue"
                        variant="subtle"
                        px={2}
                        borderRadius="full"
                        fontSize="xs"
                      >
                        {employee.reseller_name}
                      </Badge>
                    </VStack>
                  )}
                  <Divider />

                  <SimpleGrid columns={2} spacing={3}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" color="gray.500">
                        {t("employee.role")}
                      </Text>
                      <EnhancedTrueFalseIcon
                        value={employee.is_manager}
                        trueLabel={t("employee.manager_label")}
                        falseLabel={t("employee.worker_label")}
                      />
                    </VStack>

                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" color="gray.500">
                        {t("employee.login_permission")}
                      </Text>
                      <HStack>
                        <Badge
                          colorScheme={employee.login_enabled ? "green" : "red"}
                          variant="subtle"
                          px={2}
                          borderRadius="full"
                          fontSize="xs"
                        >
                          {employee.login_enabled
                            ? t("common.open")
                            : t("common.closed")}
                        </Badge>
                        {isSuperUser && (
                          <Switch
                            isChecked={employee.login_enabled}
                            onChange={() => toggleLoginEnabled(employee)}
                            colorScheme="green"
                            size="sm"
                          />
                        )}
                      </HStack>
                    </VStack>
                  </SimpleGrid>

                  <Box>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      {t("employee.teams")}
                    </Text>
                    {employee.team_memberships.length > 0 ? (
                      <Wrap spacing={2}>
                        {employee.team_memberships.map((membership, idx) => (
                          <WrapItem key={idx}>
                            <Tag
                              size="sm"
                              borderRadius="full"
                              variant="subtle"
                              colorScheme={
                                membership.is_lead ? "orange" : "purple"
                              }
                            >
                              <TagLabel>
                                {membership.team?.name || "-"}{" "}
                                {membership.is_lead && "(Lider)"}
                              </TagLabel>
                              {onRemoveFromTeam && (
                                <TagCloseButton
                                  onClick={() =>
                                    onRemoveFromTeam(employee, membership)
                                  }
                                />
                              )}
                            </Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    ) : (
                      <Badge colorScheme="gray" variant="outline">
                        {t("employee.no_team")}
                      </Badge>
                    )}
                  </Box>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            p={8}
            bg={cardBg}
            borderRadius="xl"
            border="1px"
            borderColor={borderColor}
          >
            <Icon as={FiUsers} boxSize={12} color="gray.300" mb={4} />
            <Text fontSize="md" color="gray.500" fontWeight="medium">
              {t("employee.no_employee_found")}
            </Text>
            <Text fontSize="xs" color="gray.400">
              {t("employee.try_changing_filters")}
            </Text>
          </Flex>
        )
      ) : (
        <Box
          bg={cardBg}
          borderRadius="xl"
          shadow="lg"
          overflow="hidden"
          border="1px"
          borderColor={borderColor}
        >
          {paginatedEmployees.length > 0 ? (
            <TableContainer>
              <Table variant="simple" size="md">
                <Thead bg={tableBg}>
                  <Tr>
                    {isSuperUser && (
                      <Th
                        py={4}
                        px={4}
                        borderBottom="2px"
                        borderColor={borderColor}
                      >
                        <Checkbox
                          isChecked={
                            paginatedEmployees.length > 0 &&
                            paginatedEmployees.every((e) =>
                              selectedUrls.has(e.url)
                            )
                          }
                          onChange={toggleSelectAllCurrentPage}
                        />
                      </Th>
                    )}
                    <Th
                      py={4}
                      px={6}
                      borderBottom="2px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={() => handleSort("name")}
                      _hover={{ bg: hoverBgTable }}
                    >
                      <HStack>
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={textColor}
                          textTransform="uppercase"
                          letterSpacing="wide"
                        >
                          Personel Bilgileri
                        </Text>
                        {sortConfig.key === "name" && (
                          <Icon
                            as={
                              sortConfig.direction === "asc"
                                ? FiChevronUp
                                : FiChevronDown
                            }
                          />
                        )}
                      </HStack>
                    </Th>

                    {isSuperUser && visibleCols.dealer && (
                      <Th
                        py={4}
                        px={6}
                        borderBottom="2px"
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={() => handleSort("reseller")}
                        _hover={{ bg: hoverBgTable }}
                      >
                        <HStack>
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            color={textColor}
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            {t("employee.dealer")}
                          </Text>
                          {sortConfig.key === "reseller" && (
                            <Icon
                              as={
                                sortConfig.direction === "asc"
                                  ? FiChevronUp
                                  : FiChevronDown
                              }
                            />
                          )}
                        </HStack>
                      </Th>
                    )}

                    <Th
                      py={4}
                      px={6}
                      borderBottom="2px"
                      borderColor={borderColor}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={textColor}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        {t("employee.roles")}
                      </Text>
                    </Th>
                    {visibleCols.login && (
                      <Th
                        py={4}
                        px={6}
                        borderBottom="2px"
                        borderColor={borderColor}
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={textColor}
                          textTransform="uppercase"
                          letterSpacing="wide"
                        >
                          {t("employee.login_permission")}
                        </Text>
                      </Th>
                    )}

                    {visibleCols.teams && (
                      <Th
                        py={4}
                        px={6}
                        borderBottom="2px"
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={() => handleSort("teams")}
                        _hover={{ bg: hoverBgTable }}
                      >
                        <HStack>
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            color={textColor}
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            {t("employee.teams")}
                          </Text>
                          {sortConfig.key === "teams" && (
                            <Icon
                              as={
                                sortConfig.direction === "asc"
                                  ? FiChevronUp
                                  : FiChevronDown
                              }
                            />
                          )}
                        </HStack>
                      </Th>
                    )}

                    <Th
                      py={4}
                      px={6}
                      borderBottom="2px"
                      borderColor={borderColor}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={textColor}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        {t("common.actions")}
                      </Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedEmployees.map((employee, index) => (
                    <Tr
                      key={urlToId(employee.url)}
                      _hover={{
                        bg: rowHoverBg,
                        transform: "translateX(4px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                      transition="all 0.3s ease"
                      borderBottom={
                        index === paginatedEmployees.length - 1 ? "none" : "1px"
                      }
                      borderColor={borderColor}
                    >
                      {isSuperUser && (
                        <Td py={4} px={4}>
                          <Checkbox
                            isChecked={selectedUrls.has(employee.url)}
                            onChange={() => toggleSelectOne(employee)}
                          />
                        </Td>
                      )}
                      <Td py={4} px={6}>
                        <Box
                          cursor="pointer"
                          onClick={() => openDetail(employee)}
                        >
                          <ImprovedEmployeeNameWithAvatar employee={employee} />
                        </Box>
                      </Td>

                      {isSuperUser && visibleCols.dealer && (
                        <Td py={4} px={6}>
                          <Badge
                            colorScheme="blue"
                            variant="subtle"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                          >
                            {employee.reseller_name}
                          </Badge>
                        </Td>
                      )}

                      <Td py={4} px={6}>
                        <VStack align="start" spacing={2}>
                          <EnhancedTrueFalseIcon
                            value={employee.is_manager}
                            trueLabel={t("employee.manager_label")}
                            falseLabel={t("employee.worker_label")}
                          />
                          {employee.team_memberships.some((m) => m.is_lead) && (
                            <Badge
                              colorScheme="orange"
                              variant="solid"
                              size="sm"
                            >
                              {t("employee.team_leader_label")}
                            </Badge>
                          )}
                        </VStack>
                      </Td>
                      {visibleCols.login && (
                        <Td py={4} px={6}>
                          <HStack spacing={3} align="center">
                            <Badge
                              colorScheme={
                                employee.login_enabled ? "green" : "red"
                              }
                              variant="subtle"
                              px={3}
                              py={1}
                              borderRadius="full"
                              fontSize="xs"
                            >
                              {employee.login_enabled
                                ? t("common.open")
                                : t("common.closed")}
                            </Badge>
                            {isSuperUser && (
                              <Switch
                                isChecked={employee.login_enabled}
                                onChange={() => toggleLoginEnabled(employee)}
                                colorScheme="green"
                                size="md"
                              />
                            )}
                          </HStack>
                        </Td>
                      )}

                      {visibleCols.teams && (
                        <Td py={4} px={6}>
                          {employee.team_memberships.length > 0 ? (
                            <VStack align="start" spacing={1}>
                              {employee.team_memberships.map(
                                (membership, idx) => (
                                  <HStack key={idx} spacing={2}>
                                    <Badge
                                      colorScheme={
                                        membership.is_lead ? "orange" : "purple"
                                      }
                                      variant="outline"
                                      px={2}
                                      py={1}
                                      borderRadius="full"
                                      fontSize="xs"
                                    >
                                      {membership.team?.name || "-"}
                                      {membership.is_lead && " (Lider)"}
                                    </Badge>
                                    {onRemoveFromTeam && (
                                      <IconButton
                                        size="xs"
                                        colorScheme="red"
                                        variant="ghost"
                                        icon={<MdCancel />}
                                        onClick={() =>
                                          onRemoveFromTeam(employee, membership)
                                        }
                                        aria-label={t(
                                          "employee.remove_from_team"
                                        )}
                                        title={t("employee.remove_from_team")}
                                      />
                                    )}
                                  </HStack>
                                )
                              )}
                            </VStack>
                          ) : (
                            <Badge colorScheme="gray" variant="outline">
                              {t("employee.no_team")}
                            </Badge>
                          )}
                        </Td>
                      )}

                      <Td py={4} px={6}>
                        <HStack spacing={2}>
                          <Tooltip label={t("employee.add_to_team")}>
                            <Button
                              size="sm"
                              colorScheme="green"
                              variant="outline"
                              onClick={() =>
                                onAddToTeam && onAddToTeam(employee)
                              }
                            >
                              <Icon as={FiUserPlus} />
                            </Button>
                          </Tooltip>

                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<FiMoreVertical />}
                              size="sm"
                              variant="ghost"
                              borderRadius="lg"
                            />
                            <MenuList>
                              <MenuItem
                                icon={<FiEdit3 />}
                                onClick={() =>
                                  onEditEmployee && onEditEmployee(employee)
                                }
                              >
                                {t("edit")}
                              </MenuItem>
                              {(isSuperUser || isManager) && (
                                <MenuItem
                                  icon={<FiSend />}
                                  onClick={() =>
                                    sendEmployeeCredentials(employee)
                                  }
                                >
                                  {t("employee.send_credentials")}
                                </MenuItem>
                              )}
                              {isSuperUser && (
                                <MenuItem
                                  icon={<FiSettings />}
                                  onClick={() => toggleManager(employee)}
                                >
                                  {employee.is_manager
                                    ? t("employee.remove_manager_privilege")
                                    : t("employee.make_manager")}
                                </MenuItem>
                              )}
                              {isSuperUser && (
                                <MenuItem
                                  icon={<FiUser />}
                                  onClick={() => toggleServicer(employee)}
                                >
                                  {t("employee.toggle_servicer_status")}
                                </MenuItem>
                              )}
                              <MenuItem
                                icon={<FiTrash2 />}
                                color="red.500"
                                onClick={() =>
                                  onDeleteEmployee && onDeleteEmployee(employee)
                                }
                              >
                                {t("delete")}
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Flex direction="column" align="center" justify="center" p={12}>
              <Icon as={FiUsers} boxSize={16} color="gray.300" mb={4} />
              <Text fontSize="lg" color="gray.500" fontWeight="medium">
                {t("employee.no_employee_found")}
              </Text>
              <Text fontSize="sm" color="gray.400">
                {t("employee.try_changing_filters")}
              </Text>
            </Flex>
          )}
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          num_of_items={filteredAndSortedEmployees.length}
          num_of_pages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}

      {isSuperUser && detailEmployee && (
        <Drawer
          isOpen={!!detailEmployee}
          placement="right"
          onClose={closeDetail}
          size="md"
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">
              <HStack spacing={3}>
                <Avatar
                  name={getFullName(detailEmployee.user)}
                  src={detailEmployee.user?.profile_picture}
                />
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold">
                    {getFullName(detailEmployee.user)}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {detailEmployee.user?.email}
                  </Text>
                  {detailEmployee.phone_number && (
                    <Text fontSize="sm" color="gray.500">
                      {detailEmployee.phone_number}
                    </Text>
                  )}
                </VStack>
                <Spacer />
                <HStack spacing={2}>
                  {(isSuperUser || isManager) && (
                    <Tooltip label={t("employee.send_credentials")}>
                      <IconButton
                        size="sm"
                        icon={<FiSend />}
                        onClick={() => sendEmployeeCredentials(detailEmployee)}
                        aria-label="Send credentials"
                      />
                    </Tooltip>
                  )}
                  {(isSuperUser || isManager) && (
                    <Tooltip label={t("employee.login_permission")}>
                      <Switch
                        isChecked={detailEmployee.login_enabled}
                        onChange={() => toggleLoginEnabled(detailEmployee)}
                        colorScheme="green"
                        size="sm"
                      />
                    </Tooltip>
                  )}
                  {onAddToTeam && (
                    <Tooltip label={t("employee.add_to_team")}>
                      <IconButton
                        size="sm"
                        icon={<FiUserPlus />}
                        onClick={() => onAddToTeam(detailEmployee)}
                        aria-label="Add to team"
                      />
                    </Tooltip>
                  )}
                </HStack>
              </HStack>
            </DrawerHeader>
            <DrawerBody>
              <VStack align="stretch" spacing={6} mt={4}>
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    Genel Bilgiler
                  </Text>
                  <SimpleGrid columns={2} spacing={3}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        {t("employee.dealer")}
                      </Text>
                      <Text fontSize="sm">
                        {detailEmployee.reseller_name || "-"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        {t("employee.login_permission")}
                      </Text>
                      <Text fontSize="sm">
                        {detailEmployee.login_enabled
                          ? t("common.open")
                          : t("common.closed")}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        {t("employee.role")}
                      </Text>
                      <Text fontSize="sm">
                        {detailEmployee.is_manager
                          ? t("employee.manager_label")
                          : t("employee.worker_label")}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Son giriş
                      </Text>
                      <Text fontSize="sm">
                        {(detailSummary && detailSummary.last_login) ||
                          detailEmployee.last_login ||
                          "-"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Toplam giriş sayısı
                      </Text>
                      <Text fontSize="sm">
                        {detailSummary ? detailSummary.total_logins : "-"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Son 30 gündeki giriş
                      </Text>
                      <Text fontSize="sm">
                        {detailSummary ? detailSummary.period_logins : "-"}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    Takımlar
                  </Text>
                  {(detailEmployee.team_memberships || detailEmployee.memberships || []).length > 0 ? (
                    <Wrap spacing={2}>
                      {(detailEmployee.team_memberships || detailEmployee.memberships || []).map((membership, idx) => (
                        <WrapItem key={idx}>
                          <Tag
                            size="sm"
                            borderRadius="full"
                            variant="subtle"
                            colorScheme={membership.is_lead ? "orange" : "purple"}
                          >
                            <TagLabel>
                              {membership.team?.name || "-"}{" "}
                              {membership.is_lead && "(Lider)"}
                            </TagLabel>
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  ) : (
                    <Badge colorScheme="gray" variant="outline">
                      {t("employee.no_team")}
                    </Badge>
                  )}
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    Login dağılımı (son 30 gün)
                  </Text>
                  {activitySummaryLoading && !detailSummary ? (
                    <SkeletonText noOfLines={3} spacing={2} />
                  ) : detailSummary &&
                    Array.isArray(detailSummary.daily_counts) &&
                    detailSummary.daily_counts.length > 0 ? (
                    <VStack
                      align="stretch"
                      spacing={2}
                      maxH="180px"
                      overflowY="auto"
                    >
                      {detailSummary.daily_counts.map((item, idx) => (
                        <HStack
                          key={idx}
                          justify="space-between"
                          borderWidth="1px"
                          borderRadius="md"
                          p={2}
                        >
                          <Text fontSize="xs" color="gray.600">
                            {item.day}
                          </Text>
                          <Badge colorScheme="blue" fontSize="xs">
                            {item.count} giriş
                          </Badge>
                        </HStack>
                      ))}
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Kayıtlı login aktivitesi bulunmuyor.
                    </Text>
                  )}
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    SMS Logları
                  </Text>
                  {smsLogsLoading ? (
                    <SkeletonText noOfLines={4} spacing={2} />
                  ) : smsLogs.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      Kayıtlı SMS bulunmuyor.
                    </Text>
                  ) : (
                    <VStack
                      align="stretch"
                      spacing={3}
                      maxH="260px"
                      overflowY="auto"
                    >
                      {smsLogs.map((log) => (
                        <Box
                          key={log.id}
                          borderWidth="1px"
                          borderRadius="md"
                          p={3}
                        >
                          <HStack justify="space-between" mb={1}>
                            <HStack spacing={2}>
                              <Badge
                                colorScheme={
                                  log.status === "success" ? "green" : "red"
                                }
                              >
                                {log.status === "success"
                                  ? "Başarılı"
                                  : "Hatalı"}
                              </Badge>
                              {log.template_code && (
                                <Badge variant="outline" colorScheme="blue">
                                  {log.template_code}
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize="xs" color="gray.500">
                              {log.created_at}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.600">
                            {log.body}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    Aktivite Logları
                  </Text>
                  {activityLogsLoading ? (
                    <SkeletonText noOfLines={4} spacing={2} />
                  ) : activityLogs.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      Kayıtlı aktivite bulunmuyor.
                    </Text>
                  ) : (
                    <VStack
                      align="stretch"
                      spacing={3}
                      maxH="260px"
                      overflowY="auto"
                    >
                      {activityLogs.map((log) => (
                        <Box
                          key={log.id}
                          borderWidth="1px"
                          borderRadius="md"
                          p={3}
                        >
                          <HStack justify="space-between" mb={1}>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="xs" fontWeight="bold">
                                {formatActivityAction(log.action)}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {log.actor_username} • {log.created_at}
                              </Text>
                            </VStack>
                          </HStack>
                          {(() => {
                            const ov = log?.old_value || {};
                            const nv = log?.new_value || {};
                            const hasAny = (Object.keys(ov).length + Object.keys(nv).length) > 0;
                            if (!hasAny) return null;
                            const renderVal = (obj) => {
                              if (obj && typeof obj.team_id !== 'undefined') {
                                const id = obj.team_id;
                                const nm = teamNameById[id] || `Takım #${id}`;
                                return `${nm} (#${id})`;
                              }
                              try {
                                return JSON.stringify(obj || {}, null, 2);
                              } catch {
                                return String(obj);
                              }
                            };
                            return (
                              <Box mt={2}>
                                <Text fontSize="xs" color="gray.500" mb={1}>
                                  Değişiklik
                                </Text>
                                <HStack align="flex-start" spacing={3}>
                                  <Box flex={1}>
                                    <Text fontSize="xs" color="gray.500">
                                      Önce
                                    </Text>
                                    <Text
                                      fontSize="xs"
                                      whiteSpace="pre-wrap"
                                      fontFamily="mono"
                                    >
                                      {renderVal(ov)}
                                    </Text>
                                  </Box>
                                  <Box flex={1}>
                                    <Text fontSize="xs" color="gray.500">
                                      Sonra
                                    </Text>
                                    <Text
                                      fontSize="xs"
                                      whiteSpace="pre-wrap"
                                      fontFamily="mono"
                                    >
                                      {renderVal(nv)}
                                    </Text>
                                  </Box>
                                </HStack>
                              </Box>
                            );
                          })()}
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </Box>
  );
}
